import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listRequisitions } from '../api/vendorApi';
import { VpSpinner, VpEmpty, VpStatusBadge, VpPagination, fmtDate } from '../components/VpUI';

const PER_PAGE = 10;

const VendorRequisitions = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [rows, setRows] = useState<any[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [quoteStatus, setQuoteStatus] = useState('');
	const [error, setError] = useState('');

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const data = await listRequisitions({
				pageNo: page,
				perPage: PER_PAGE,
				search: search || undefined,
				quoteStatus: quoteStatus || undefined,
			});
			setRows(data?.records || []);
			setTotal(data?.totalRecords || 0);
		} catch (err: any) {
			setError(err?.apiMessage || 'Could not load requisitions.');
		} finally {
			setLoading(false);
		}
	}, [page, search, quoteStatus]);

	useEffect(() => {
		load();
	}, [load]);

	const applyFilters = () => {
		setPage(1);
	};

	if (error) return <div className='vp-alert vp-alert-error'>{error}</div>;

	return (
		<div className='vp-card'>
			<div className='vp-card-head'>
				<h3>Assigned Requisitions</h3>
				<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
					<input
						className='vp-input vp-input-sm'
						style={{ width: 180 }}
						placeholder='Search requisition no'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
					/>
					<select
						className='vp-input vp-input-sm'
						style={{ width: 150 }}
						value={quoteStatus}
						onChange={(e) => {
							setQuoteStatus(e.target.value);
							setPage(1);
						}}>
						<option value=''>All</option>
						<option value='not_quoted'>Not quoted</option>
						<option value='quoted'>Quoted</option>
					</select>
					<button
						type='button'
						className='vp-btn vp-btn-ghost vp-input-sm'
						onClick={applyFilters}>
						Search
					</button>
				</div>
			</div>
			{loading ? (
				<VpSpinner />
			) : rows.length === 0 ? (
				<VpEmpty icon='requisitions' text='No requisitions found.' />
			) : (
				<>
					<div className='vp-table-wrap'>
						<table className='vp-table'>
							<thead>
								<tr>
									<th>Requisition No</th>
									<th>Date</th>
									<th>Items</th>
									<th>Quotation</th>
									<th style={{ textAlign: 'right' }}>Action</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((r) => (
									<tr key={r._id}>
										<td style={{ fontWeight: 600 }}>{r.requisition_no}</td>
										<td>{fmtDate(r.date || r.createdAt)}</td>
										<td>{r.products?.length ?? 0}</td>
										<td>
											{r.alreadyQuoted ? (
												<VpStatusBadge
													status={r.myQuotation?.quotation_status}
												/>
											) : (
												<span className='vp-badge vp-badge-neutral'>
													Not quoted
												</span>
											)}
										</td>
										<td style={{ textAlign: 'right' }}>
											{r.alreadyQuoted ? (
												<button
													type='button'
													className='vp-btn vp-btn-ghost vp-input-sm'
													onClick={() =>
														navigate(
															`/vendor-portal/quotations/${r.myQuotation?._id}`,
														)
													}>
													View quote
												</button>
											) : (
												<button
													type='button'
													className='vp-btn vp-btn-primary vp-input-sm'
													onClick={() =>
														navigate(
															`/vendor-portal/requisitions/${r._id}`,
														)
													}>
													Submit quotation
												</button>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<VpPagination page={page} perPage={PER_PAGE} total={total} onPage={setPage} />
				</>
			)}
		</div>
	);
};

export default VendorRequisitions;
